import { DataModelSchemaDetail } from 'common/types/kraken-core/DataModelSchema'
import {
  DataModelSchemaResourceDetail,
  DataModelSchemaResourceList,
} from 'common/types/kraken-core/DataModelSchemaResource'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import { UseMutationResult, UseQueryResult, useMutation, useQuery } from 'react-query'
import {
  createDataModelSchema,
  deleteDataModelSchema,
  getDataModelSchema,
  listDataModelSchema,
  updateDataModelSchema,
  validateDataModelSchemaFile,
  generateDataModelSchema,
  listDataModelSchemaResources,
  getDataModelSchemaResource,
  createDataModelSchemaResource,
  updateDataModelSchemaResource,
  deleteDataModelSchemaResource,
  downloadDataModelSchemaResource,
  previewDataModelSchemaResource,
} from 'services/kraken-core/data_model/dataModel.service'
import {
  ListDataModelSchemaEnginesRequest,
  ListDataModelSchemaReponse,
  ListDataModelSchemaRequest,
  ValidateDataModelSchemaFileResponse,
  GenerateDataModelSchemaRequest,
  GenerateDataModelSchemaResponse,
  ListDataModelSchemaResourceRequest,
  CreateDataModelSchemaResourceRequestType,
  UpdateDataModelSchemaResourceRequestType,
} from 'services/kraken-core/data_model/types'
import { UseValidateModelSchemaFile } from './types'

// X12 Schema Generation Types
export interface X12SchemaGenerationRequest {
  dataModelSchemaId?: string
  resourceFiles: File[]
  customizations: string
}

export interface X12SchemaGenerationResponse {
  schema: string | null
  message: string
}

// X12 Schema Generation Service
export const generateX12Schema = async (
  request: X12SchemaGenerationRequest
): Promise<[X12SchemaGenerationResponse | null, Error | null]> => {
  // Convert to the new API format
  const generateRequest: GenerateDataModelSchemaRequest = {
    engine: 'application/EDI-X12',
    prompt: request.customizations,
    files: request.resourceFiles,
    data_model_schema_id: request.dataModelSchemaId,
  }

  const { error, response } = await generateDataModelSchema(generateRequest)

  // Check if there was an error in the HTTP request
  if (error) {
    // If it's an HTTP error with response data, extract the actual error message
    if (error.response?.data) {
      const backendMessage =
        error.response.data.message || error.response.data.detail || error.response.data.error
      if (backendMessage) {
        return [null, new Error(backendMessage)]
      }
    }
    // If it's an HTTP error but no specific message, use the status text or default message
    if (error.response?.status) {
      const statusText = error.response.statusText || `HTTP ${error.response.status}`
      const backendMessage =
        error.response.data?.message ||
        error.response.data?.detail ||
        error.response.data?.error ||
        error.message
      return [null, new Error(`${statusText}: ${backendMessage}`)]
    }
    // Fallback to the original error
    return [null, error]
  }

  // Check if response exists
  if (!response?.data) {
    return [null, new Error('Invalid response from schema generation service')]
  }

  const responseData = response.data

  // If schema is null but we have an error message, return error
  if (!responseData.schema && responseData.message) {
    return [null, new Error(responseData.message)]
  }

  // Return the response data
  return [responseData, null]
}

// X12 Schema Generation Hook
export const useGenerateX12Schema = (): UseMutationResult<
  [X12SchemaGenerationResponse | null, Error | null],
  unknown,
  X12SchemaGenerationRequest,
  unknown
> => {
  const mutation = useMutation(async (request: X12SchemaGenerationRequest) => {
    return await generateX12Schema(request)
  })
  return mutation
}

export const useGetDataModelSchema = (
  id: string,
  config?: any
): UseQueryResult<DataModelSchemaDetail> => {
  const queryFn = async (id: string) => {
    const { response } = await getDataModelSchema(id)
    return response?.data
  }
  const query = useQuery(['data_model/schemas/get', id], () => queryFn(id), { ...config })
  return query
}

export const useSearchDataModelSchemas = (
  params: ListDataModelSchemaEnginesRequest,
  config?: any
): UseQueryResult<PaginatedResponse<ListDataModelSchemaReponse>> => {
  const queryFn = async (filters: ListDataModelSchemaRequest) => {
    const response = await listDataModelSchema(filters)
    return response
  }
  const query = useQuery(['data_model/schemas/get', params], () => queryFn(params), { ...config })
  return query
}

export const useUpdateDataModelSchema = (): UseMutationResult<
  any,
  unknown,
  DataModelSchemaDetail,
  unknown
> => {
  const mutation = useMutation(async (params: DataModelSchemaDetail) => {
    const { response } = await updateDataModelSchema({ ...params })
    return response?.data
  })
  return mutation
}

export const useCreateDataModelSchema = (): UseMutationResult<
  any,
  unknown,
  DataModelSchemaDetail,
  unknown
> => {
  const mutation = useMutation(async (params: DataModelSchemaDetail) => {
    const { response } = await createDataModelSchema({ ...params })
    return response?.data
  })
  return mutation
}

export const useDeleteDataModelSchema = (): UseMutationResult<any, unknown, string, unknown> => {
  const mutation = useMutation(async (id: string) => {
    const { response } = await deleteDataModelSchema(id)
    return response?.data
  })
  return mutation
}

export const useValidateDataModelSchemaFile = (): UseMutationResult<
  ValidateDataModelSchemaFileResponse,
  unknown,
  UseValidateModelSchemaFile,
  unknown
> => {
  const mutation = useMutation(async (params: UseValidateModelSchemaFile) => {
    const { response } = await validateDataModelSchemaFile({
      dataModelSchemaId: params.dataModelSchemaId,
      file: params.file,
    })
    return response?.data
  })
  return mutation
}

export const useGenerateDataModelSchema = (): UseMutationResult<
  GenerateDataModelSchemaResponse,
  unknown,
  GenerateDataModelSchemaRequest,
  unknown
> => {
  const mutation = useMutation(async (params: GenerateDataModelSchemaRequest) => {
    const { response } = await generateDataModelSchema(params)
    return response?.data
  })
  return mutation
}

// Data Model Schema Resource Hooks
export const useListDataModelSchemaResources = (
  params: ListDataModelSchemaResourceRequest,
  config?: any
): UseQueryResult<PaginatedResponse<DataModelSchemaResourceList>> => {
  const queryFn = async (filters: ListDataModelSchemaResourceRequest) => {
    const response = await listDataModelSchemaResources(filters)
    return response
  }
  const query = useQuery(['data_model/schemas/resources/list', params], () => queryFn(params), {
    ...config,
  })
  return query
}

export const useGetDataModelSchemaResource = (
  id: string,
  config?: any
): UseQueryResult<DataModelSchemaResourceDetail> => {
  const queryFn = async (id: string) => {
    const { response } = await getDataModelSchemaResource(id)
    return response?.data
  }
  const query = useQuery(['data_model/schemas/resources/get', id], () => queryFn(id), { ...config })
  return query
}

export const useCreateDataModelSchemaResource = (): UseMutationResult<
  any,
  unknown,
  CreateDataModelSchemaResourceRequestType,
  unknown
> => {
  const mutation = useMutation(async (params: CreateDataModelSchemaResourceRequestType) => {
    const { response } = await createDataModelSchemaResource(params)
    return response?.data
  })
  return mutation
}

export const useUpdateDataModelSchemaResource = (): UseMutationResult<
  any,
  unknown,
  { id: string; params: UpdateDataModelSchemaResourceRequestType },
  unknown
> => {
  const mutation = useMutation(
    async ({ id, params }: { id: string; params: UpdateDataModelSchemaResourceRequestType }) => {
      const { response } = await updateDataModelSchemaResource(id, params)
      return response?.data
    }
  )
  return mutation
}

export const useDeleteDataModelSchemaResource = (): UseMutationResult<
  any,
  unknown,
  string,
  unknown
> => {
  const mutation = useMutation(async (id: string) => {
    const { response } = await deleteDataModelSchemaResource(id)
    return response?.data
  })
  return mutation
}

export const useDownloadDataModelSchemaResource = (): UseMutationResult<
  Blob,
  unknown,
  string,
  unknown
> => {
  const mutation = useMutation(async (id: string) => {
    const { response } = await downloadDataModelSchemaResource(id)
    return response?.data
  })
  return mutation
}

export const usePreviewDataModelSchemaResource = (): UseMutationResult<
  any,
  unknown,
  string,
  unknown
> => {
  const mutation = useMutation(async (id: string) => {
    const { response } = await previewDataModelSchemaResource(id)
    return response?.data
  })
  return mutation
}
